import React from 'react';
import { gateSteps } from '../../common/assessment';

// The applicability gate's working, step by step.
//
// Showing the authored step text beside the result it produced is what makes a mis-worded pre-check
// obvious: an instruction-shaped step ("mark Yes and stop") sitting next to a verdict that
// contradicts it reads wrong at a glance, where the verdict alone would not.
//
// Shared by the Compliance and Conduct sections rather than duplicated into each. The two rendered
// the same thing from two copies once already, and they drifted.

const RESULT_BADGES = {
  true: 'bg-green-100 text-green-700',
  false: 'bg-red-100 text-red-700',
  unknown: 'bg-amber-100 text-amber-800',
};

const StepResult = ({ result }) => {
  if (!result) {
    // The gate stops at the first step that does not hold, so later steps have no result at all.
    return <span className="shrink-0 text-xs text-gray-400">not reached</span>;
  }
  const tone = RESULT_BADGES[result] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`shrink-0 text-xs px-2 py-0.5 rounded font-semibold ${tone}`}>
      {result.toUpperCase()}
    </span>
  );
};

// Answers that mean "this question did not apply", matching NA_ANSWERS in the two scorers.
const NA = ['n/a', 'na', 'n\\a', 'not applicable'];

// True when a corrected step and the answer now contradict each other.
//
// Two shapes, both this feature's own contradiction inverted:
//   step true  + answer n/a   -> the record says it applies, the verdict says it did not
//   step false + answer yes/no -> the record says it never applied, yet here is a verdict
//
// Advisory only. A reviewer may deliberately record that the gate was wrong without re-judging the
// call, so this never blocks a save - it just stops the easy mistake of fixing the record and
// forgetting the verdict.
export const stepAnswerConflict = (edited, answer) => {
  const corrections = Object.values(edited || {});
  if (corrections.length === 0) return null;

  const isNa = NA.includes(String(answer ?? '').trim().toLowerCase());
  const results = corrections.map(c => c.result).filter(Boolean);

  if (isNa && results.includes('true')) {
    return 'You corrected a pre-check to True, but the answer is still N/A. '
         + 'The question will stay recorded as not applying.';
  }
  if (!isNa && results.includes('false')) {
    return 'You marked a pre-check as not holding, but the answer is still a verdict. '
         + 'The question will stay scored.';
  }
  return null;
};

// The same steps, editable.
//
// A reviewer who checks the transcript by hand can correct a step the gate got wrong. This records
// what they found - it does not re-derive the verdict, which still comes from the answer they set
// alongside it. The step is the record; the answer is the decision.
//
// `edited` holds ONLY the steps the reviewer touched, and onChange adds to it. That is deliberate:
// the API stamps overriddenBy on every step it receives, so sending untouched ones would record the
// gate's own findings as human decisions.
export const PreCheckStepsEditor = ({ parsed, edited = {}, onChange, disabled = false }) => {
  const steps = gateSteps(parsed);
  if (steps.length === 0) return null;

  return (
    <div className="border-t pt-2 mt-2">
      <div className="text-xs text-gray-500 mb-1">
        Pre-checks
        <span className="ml-1 text-gray-400">— correct one the gate got wrong</span>
      </div>
      <ol className="space-y-2">
        {steps.map(step => {
          const change = edited[step.number] || {};
          const result = change.result !== undefined ? change.result : (step.result || '');
          const reason = change.reason !== undefined ? change.reason : (step.reason || '');
          const touched = change.result !== undefined || change.reason !== undefined;

          return (
            <li
              key={step.number}
              className={`border rounded p-2 ${touched ? 'border-blue-300 bg-blue-50' : 'border-gray-100'}`}
            >
              <div className="text-xs text-gray-700 mb-1">
                <span className="font-medium">Step {step.number}.</span>{' '}
                {step.text || <em className="text-gray-400">no step text stored</em>}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={result}
                  onChange={(e) => onChange(step.number, 'result', e.target.value)}
                  className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  disabled={disabled}
                  aria-label={`Step ${step.number} result`}
                >
                  {/* The gate stops at the first step that fails, so a later step legitimately has
                      no result. That has to stay representable rather than forced to a value. */}
                  <option value="">not reached</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                  <option value="unknown">Unknown</option>
                </select>
                {touched && <span className="text-xs text-blue-700">✎ corrected</span>}
              </div>
              <input
                type="text"
                value={reason}
                onChange={(e) => onChange(step.number, 'reason', e.target.value)}
                className="w-full mt-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="What did you find in the transcript?"
                disabled={disabled}
                aria-label={`Step ${step.number} reason`}
              />
              {/* The gate's reasoning stays on screen while it is being overruled - a reviewer
                  should be able to see what the machine concluded, not have it vanish. */}
              {touched && step.reason && (
                <div className="text-xs text-gray-500 italic mt-1">Gate said: {step.reason}</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export const PreCheckSteps = ({ parsed, label = 'Pre-checks' }) => {
  const steps = gateSteps(parsed);

  if (steps.length === 0) {
    // Verdicts written before the per-step breakdown existed carry only the failing step number.
    if (parsed?.applicabilityFailedStep) {
      return (
        <li className="text-xs text-gray-500">
          • Pre-check step {parsed.applicabilityFailedStep} did not hold
        </li>
      );
    }
    return null;
  }

  return (
    <li className="mt-2">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <ol className="space-y-2">
        {steps.map(step => (
          <li
            key={step.number}
            className={`border rounded p-2 ${
              step.number === parsed?.applicabilityFailedStep
                ? 'border-gray-300 bg-white'
                : 'border-gray-100'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs text-gray-700">
                <span className="font-medium">Step {step.number}.</span>{' '}
                {step.text || <em className="text-gray-400">no step text stored</em>}
              </span>
              <StepResult result={step.result} />
            </div>
            {step.reason && (
              <div className="text-xs text-gray-600 mt-1">{step.reason}</div>
            )}
            {/* A reviewer corrected this step. Keep the gate's original reasoning visible beneath
                it - the point of showing steps is that a human can see the machine was wrong, which
                is lost if the machine's version disappears. */}
            {step.overriddenBy && (
              <div className="text-xs text-blue-700 mt-1">
                ✎ Corrected by {step.overriddenBy}
                {step.originalReason && (
                  <span className="block text-gray-500 italic">
                    Gate originally said: {step.originalReason}
                  </span>
                )}
              </div>
            )}
            {step.agent && (
              <div className="text-xs text-gray-600 mt-1">• Agent: "{step.agent}"</div>
            )}
            {step.customer && (
              <div className="text-xs text-gray-600 mt-1">• Customer: "{step.customer}"</div>
            )}
          </li>
        ))}
      </ol>
    </li>
  );
};

export default PreCheckSteps;
